<template>
  <AppDialog
    v-model="localIsOpen"
    class="tw-w-screen lg:tw-w-[60vw] tw-max-w-[800px]"
    is-modal
    title="Pick an Image"
  >
    <div
      v-if="!selectedImage && !showBrowser"
      class="tw-flex tw-flex-wrap tw-items-center tw-gap-2"
    >
      <AppImageUploader
        class="tw-grow md:tw-grow-0"
        :default-license-id="defaultLicenseId"
        @input="setImage"
      />
      <button
        type="button"
        class="btn btn-sm btn-secondary tw-flex tw-items-center tw-grow md:tw-grow-0"
        @click.stop="showBrowser = true"
      >
        <icon-carbon-search class="tw-mr-1 tw-w-6 tw-h-6" />
        Bilder durchsuchen
      </button>
      <button
        v-if="currentImage"
        type="button"
        class="btn btn-sm btn-secondary tw-flex tw-items-center tw-grow md:tw-grow-0"
        @click.stop="selectedImage = currentImage"
      >
        <icon-carbon-pen class="tw-mr-1 tw-w-6 tw-h-6" />
        Derzeitiges Bild bearbeiten
      </button>
    </div>

    <AppImageBrowser v-if="showBrowser" :endpoint="IMAGE_API_ENDPOINT" @input="setImage" />

    <AppImageEditor
      v-if="licenses.length > 0 && selectedImage"
      v-model="selectedImage"
      :licenses="licenses"
    />

    <template v-if="showBrowser || selectedImage" #footer>
      <div class="tw-flex tw-gap-x-6">
        <button
          v-if="selectedImage"
          :disabled="isSaving"
          type="button"
          class="btn btn-primary"
          @click.stop="selectedImage && saveAndSelectImage(selectedImage)"
        >
          <template v-if="!selectedImage.id || selectedImage.id !== props.modelValue">
            Dieses Bild wählen
          </template>
          <template v-else> Änderungen speichern </template>
        </button>
        <button
          v-if="selectedImage"
          type="button"
          class="btn"
          @click.stop="selectedImage = undefined"
        >
          Abbrechen
        </button>
        <button
          v-if="showBrowser"
          type="button"
          class="btn btn-secondary"
          @click.stop="showBrowser = false"
        >
          Abbrechen
        </button>
      </div>
    </template>
  </AppDialog>
</template>

<script lang="ts" setup>
import { useAsyncState } from '@vueuse/core'
import { computed, ref } from 'vue'
import { Image, Keys, License } from '../types'
import AppDialog from './AppDialog.vue'
import AppImageBrowser from './AppImageBrowser.vue'
import AppImageEditor from './AppImageEditor.vue'
import AppImageUploader from './AppImageUploader.vue'
import { getCookie, objectToFormData, useAsyncFunction, useUpdatableState } from './util'

const IMAGE_API_ENDPOINT = '/api/internal/images/'

const props = defineProps<{
  isOpen: boolean
  modelValue: number | undefined
  defaultLicenseId: number
}>()
const emit = defineEmits<{
  (e: 'show', state: boolean): void
  (e: 'update:modelValue', value: number | undefined): void
}>()

const localIsOpen = useUpdatableState(
  computed(() => props.isOpen),
  (isOpen) => emit('show', isOpen),
)
const showBrowser = ref(false)
const selectedImage = ref<Image | undefined>()
const { state: licenses } = useAsyncState<License[]>(
  () => fetch('/api/internal/licenses/').then((res) => res.json().then(({ results }) => results)),
  [],
)
const { state: currentImage } = useAsyncState<Image | undefined>(
  () =>
    props.modelValue
      ? fetch(`${IMAGE_API_ENDPOINT}${props.modelValue}/`).then((res) => res.json())
      : Promise.resolve(undefined),
  undefined,
)
const { isLoading: isSaving, fn: saveAndSelectImage } = useAsyncFunction(async function (
  image: Image,
) {
  const modifiableKeys: Keys<Image> = [
    'imageFile',
    'title',
    'description',
    'licenseId',
    'authorName',
    'originUrl',
    'isUseExplicitlyGrantedByAuthor',
  ]
  const { url, method } = image.id
    ? { url: `${IMAGE_API_ENDPOINT}${image.id}/`, method: 'put' }
    : { url: IMAGE_API_ENDPOINT, method: 'post' }
  const headers = new Headers()
  const csrf = getCookie('csrftoken')
  if (csrf) headers.set('x-csrftoken', csrf)
  const formData = objectToFormData(image, modifiableKeys)
  const res = await fetch(url, { method, headers, body: formData })
  if (res.ok) {
    const data: Image = await res.json()
    emit('update:modelValue', data.id)
    emit('show', false)
  }
})

function setImage(image?: Image) {
  selectedImage.value = image
  showBrowser.value = false
}
</script>
